import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import {
  DataEntityOption,
  DisplayEntityValueDataEntity
} from 'src/app/models/data-entities';
import {
  Utilities,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';

import { DataEntityEditorBaseComponent } from '../../data-entity-editor-base/data-entity-editor-base.component';
import { TableColumn } from '@swimlane/ngx-datatable';
import { GridSettings } from 'src/app/models/grid-settings';
import { Filter, FilterBuilderOutput, FilterBuilderParam } from 'src/app/models/filter-builder';

@Component({
    selector: 'wm-display-entity-value-data-entity-editor',
    templateUrl: './display-entity-value-data-entity-editor.component.html',
    styleUrls: ['./display-entity-value-data-entity-editor.component.css'],
    standalone: false
})
export class DisplayEntityValueDataEntityEditorComponent
  extends DataEntityEditorBaseComponent
  implements OnInit, OnDestroy { 
  
  displayEntityValue: DisplayEntityValueDataEntity;
  useDataEntityLabel = true;
  hideLabelAndValue = false;
  selectedOption: string;
  existingSelection = [];
  loading = true;
  columns: TableColumn[];
  rows: DataEntityOption[];
  page = {
    // The number of elements in the page
    size: 10,
    // The total number of elements
    totalElements: 0,
    // The total number of pages
    totalPages: 0,
    // The current page number
    pageNumber: 0
  };
  sortField: string;
  sortDescending: boolean;
  params: FilterBuilderParam[] = [
    {
      id: 'templateCode',
      name: 'Template Code'
    },
    {
      id: 'label',
      name: 'Label'
    },
    {
      id: 'activityName',
      name: 'Activity'
    }
  ];
  filters: FilterBuilderOutput = {};
  entityRefConfigured = false;
  searchOpened = false;

  constructor(
    @Inject(forwardRef(() => WorkflowService))
      public _workflowSvc: WorkflowService,
    @Inject(forwardRef(() => WorkflowContextService))
      private _context: WorkflowContextService,
    private _fb: UntypedFormBuilder,
    private ref: ChangeDetectorRef
  ) {
    super();
  }

  getId = row => {
    return row && row.templateCode;
  };

  ngOnInit() {
    this.columns = [
      {
        prop: 'templateCode',
        name: 'Template Code',
      },
      {
        prop: 'label',
        name: 'Label'
      },
      {
        prop: 'activityName',
        name: 'Activity'
      }
    ];

    this.displayEntityValue = this.dataEntity as DisplayEntityValueDataEntity;

    const tCode = this.displayEntityValue.templateCode;

    this.useDataEntityLabel =
      this.displayEntityValue.useDataEntityLabel == null ||
      this.displayEntityValue.useDataEntityLabel === true;

    const existingReferenceEntity = this.activity.model
      .getEntities()
      .find(f => f.templateCode === tCode);

    if (existingReferenceEntity === undefined) {
      this.useDataEntityLabel = false;
      this.displayEntityValue.useDataEntityLabel = false;
      this.hideLabelAndValue = false;
      this.displayEntityValue.hideLabelAndValueWhenNullorEmpty = false;
    }

    this.hideLabelAndValue =
      this.displayEntityValue.hideLabelAndValueWhenNullorEmpty === true;

    this.form.addControl(
      'display-entity-value-select',
      this._fb.control(false, [Validators.required])
    );

    this.form.controls['display-entity-value-select'].setValue(null);

    if (this.displayEntityValue && this.displayEntityValue.templateCodeRef && this.displayEntityValue.templateCodeRef !== '') {
      this.resolveExistingDataEntityRef();
    }

    this.form.addControl(
      'use-data-entity-label-check',
      this._fb.control(false)
    );

    this.form.addControl(
      'hide-label-and-value-when-null-or-empty-check',
      this._fb.control(false)
    );
  }

  ngOnDestroy() {
    this.form.removeControl('display-entity-value-select');
    this.form.removeControl('use-data-entity-label-check');
    this.form.removeControl('hide-label-and-value-when-null-or-empty-check');
  }

  openSearch() {
    this.searchOpened = true;

    this.ref.detectChanges();
  }

  resolveExistingDataEntityRef() {
    this._workflowSvc.getDataEntitiesBeforeMe(
      this._context.workflow, 
      this.activity, 
      null, 
      [ WorkflowService.DATA_ENTITIES.DisplayEntityValue.code ]
    ).subscribe(entities => {
      if (entities && entities.length > 0) {
        let existingRef = entities.find(i => i.templateCode === this.displayEntityValue.templateCodeRef);

        if (existingRef) {
          this.selectedOption = `${existingRef.templateCode}${(existingRef.activityLabel ? ' (' + existingRef.activityLabel + ')' : '')}`

          this.existingSelection = [{
            id: existingRef.templateCode,
            templateCode: existingRef.templateCode,
            label: existingRef.label,
            activityName: existingRef.activityLabel
          }];

          this.form.controls['display-entity-value-select'].setValue(this.displayEntityValue.templateCodeRef);

          this.entityRefConfigured = true;
        } else {
          this.selectedOption = '(None selected)';
        }
      } else {
        this.selectedOption = '(None selected)';
      }
    });
  }

  getEntityOptions() {
    this.loading = true;

    this._workflowSvc.getDataEntitiesBeforeMe(
      this._context.workflow, 
      this.activity, 
      null, 
      [ WorkflowService.DATA_ENTITIES.DisplayEntityValue.code ]
    ).subscribe(entities => {
      if (entities) {
        let entityOptions = entities.map(e => {
          return new DataEntityOption({
            id: e.templateCode,
            templateCode: e.templateCode,
            label:  e.label && e.label.length > 25 ? e.label.slice(0, 24) + "\u2026" : e.label,
            activityName: e.activityLabel
          })
        });

        if (!this.selectedOption) {
          if (this.displayEntityValue && this.displayEntityValue.templateCodeRef && this.displayEntityValue.templateCodeRef !== '') {
            let existingRef = entities.find(i => i.templateCode === this.displayEntityValue.templateCodeRef);
  
            if (existingRef) {
             this.selectedOption = `${existingRef.templateCode}${(existingRef.activityLabel ? ' (' + existingRef.activityLabel + ')' : '')}`

             this.existingSelection = [{
              id: existingRef.templateCode,
              templateCode: existingRef.templateCode,
              label: existingRef.label,
              activityName: existingRef.activityLabel
             }];
            } else {
              this.selectedOption = '(None selected)';
            }
          } else {
            this.selectedOption = '(None selected)';
          }
        }

        const filters: Filter[] = [];

        for (const id in this.filters) {
          if (this.filters[id]) {
            filters.push(this.filters[id]);
          }
        }

        const containsFilters = filters.filter(f => f.type === 'contains');

        if (containsFilters && containsFilters.length > 0) {
          const representativeFilter = containsFilters[0];
        
          const simpleSearchString = representativeFilter.value.toLowerCase();
        
          if (simpleSearchString) {
            entityOptions = entityOptions.filter(eo =>
              (eo.templateCode && eo.templateCode.toLowerCase().includes(simpleSearchString)) 
              || (eo.label && eo.label.toLowerCase().includes(simpleSearchString))
              || (eo.activityName && eo.activityName.toLowerCase().includes(simpleSearchString)))
          }
        }

        this.sortField = this.sortField || '';

        if (this.sortDescending) {
          switch (this.sortField) {
            case 'templateCode':
              entityOptions.sort((a, b) => (a.templateCode.toLowerCase() < b.templateCode.toLowerCase() ? 1 : -1));
              break;
            case 'label':
              entityOptions.sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? 1 : -1));
              break;
            case 'activity':
              entityOptions.sort((a, b) => (a.activityName.toLowerCase() < b.activityName.toLowerCase() ? 1 : -1));
              break;
            default:
              entityOptions.sort((a, b) => (a.templateCode.toLowerCase() < b.templateCode.toLowerCase() ? 1 : -1));
              break;
          }
        }
        else {
          switch (this.sortField) {
            case 'templateCode':
              entityOptions.sort((a, b) => (a.templateCode.toLowerCase() > b.templateCode.toLowerCase() ? 1 : -1));
              break;
            case 'label':
              entityOptions.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
              break;
            case 'activity':
              entityOptions.sort((a, b) => (a.activityName.toLowerCase() > b.activityName.toLowerCase() ? 1 : -1));
              break;
            default:
              entityOptions.sort((a, b) => (a.templateCode.toLowerCase() > b.templateCode.toLowerCase() ? 1 : -1));
              break;
          }
        }

        const pageCount = entityOptions.length > 0 ? (entityOptions.length - 1 + this.page.size) / this.page.size : 0;

        const pageNumberIndex = (pageCount < this.page.pageNumber + 1) ? (pageCount - 1) : this.page.pageNumber;

        const firstRecordIndex = pageNumberIndex * this.page.size;

        const pagedEntities = entityOptions.slice(firstRecordIndex, firstRecordIndex + this.page.size);

        this.page.totalElements = entityOptions.length;

        this.page.totalPages = pageCount;

        this.rows = pagedEntities.map(i => {
            i['templateCode'] = i.templateCode;
            i['label'] = i.label;
            i['activityName'] = i.activityName;

            return i;
          });
      } else {
        this.page.totalElements = 0;

        this.page.totalPages = 0;

        this.rows = [];        
      }

      this.loading = false;
    });
  }

  handleSettingsChanged(event: GridSettings) {
    this.filters = event.filters;
    this.page.size = event.pageSize;
    this.page.pageNumber = event.pageNumber;
    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending;

    this.getEntityOptions();
  }

  onSelectedChanged(selected: DataEntityOption[]) {
    const newSelectedOption = selected && selected.length > 0 ? selected[0] : null;

    if (newSelectedOption) {
      this.displayEntityValue.templateCodeRef = newSelectedOption.templateCode;

      this.form.controls['display-entity-value-select'].setValue(newSelectedOption.templateCode);

      this.selectedOption = 
        `${newSelectedOption.templateCode}${(newSelectedOption.activityName ? ' (' + newSelectedOption.activityName + ')' : '')}`;
    }
  }
}
